import { Grid, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { addDays, startOfWeek } from "date-fns";
import React, { useCallback } from "react";
import { Choose } from "react-extras";
import { useTranslation } from "react-i18next";
import { useBerthApi, usePortApi } from "~/api/GeneratedApi";
import { WeekDatePicker } from "~/components/WeekDatePicker";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { PortBerthOrderCard } from "./PortBerthOrderCard";

interface Props {
  portId: number;
  containerHeight?: number;
}

interface Weekday {
  date: Date;
}

export const PortBerthOrders = ({
  portId,
  containerHeight,
}: Props): JSX.Element => {
  const portApi = usePortApi();
  const berthApi = useBerthApi();
  const { t } = useTranslation("port-berth-orders");
  const dateFormatter = useDateFormatter();
  const [date, setDate] = React.useState<Date>(
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [weekdays, setWeekdays] = React.useState<Weekday[]>([]);

  const { data: port } = useCustomQuery({ key: "port", id: portId }, () =>
    portApi.getPort(portId),
  );

  const { data: berths } = useCustomQuery(
    { key: "berths-for-port", id: portId },
    () => berthApi.getBerths({ id: portId }),
    { select: (data) => data.results ?? [] },
  );

  const generateWeekdays = useCallback((pickedDate) => {
    const days: Weekday[] = [];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const weekStart = startOfWeek(pickedDate, { weekStartsOn: 1 });
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i);
      days.push({
        date: d,
      });
    }
    return days;
  }, []);

  React.useEffect(() => {
    setWeekdays(generateWeekdays(date));
  }, [date, generateWeekdays]);

  const datePickerCallback = useCallback((pickedDate) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setDate(pickedDate);
  }, []);

  return (
    <Box
      sx={{
        padding: 2,
        width: "100%",
        height: `${containerHeight}px`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Choose>
        <Choose.When condition={berths?.length === 0}>
          <Typography variant="h6">{t("no-berths")}</Typography>
        </Choose.When>
        <Choose.Otherwise>
          <Box sx={{ marginBottom: 1 }}>
            <Grid container alignItems="center">
              <Typography component="span" variant="h6">
                {port?.name}
              </Typography>
              <Grid item sx={{ marginLeft: "auto" }}>
                <WeekDatePicker date={date} onDateChange={datePickerCallback} />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              width: "100%",
              minWidth: 300,
              overflow: "auto",
              maxHeight: "100%",
            }}
          >
            <Box component="table">
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    sx={{
                      width: "125px",
                      fontWeight: "500",
                      position: "sticky",
                      left: "0",
                      paddingRight: 1,
                      backgroundColor: "#eaeff1",
                      zIndex: 1,
                    }}
                  >
                    {t("berths:berths")}
                  </Box>
                  {weekdays.map((x) => (
                    <Box
                      key={x.date.valueOf()}
                      component="th"
                      sx={{ fontWeight: "500" }}
                    >
                      {dateFormatter({ date: x.date, format: "cccccc d.M" })}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {(berths ?? []).map((berth) => (
                  <Box key={berth.id} component="tr">
                    <Box
                      component="td"
                      sx={{
                        width: "125px",
                        position: "sticky",
                        left: "0",
                        backgroundColor: "#eaeff1",
                        zIndex: 1,
                        padding: 1,
                        margin: 1,
                      }}
                    >
                      {berth.name}
                    </Box>
                    {weekdays.map((day) => (
                      <Box key={day.date.valueOf()} component="td">
                        <PortBerthOrderCard
                          berthId={berth.id}
                          date={day.date}
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Choose.Otherwise>
      </Choose>
    </Box>
  );
};
